import gql from 'graphql-tag';
import {useMemo, useRef} from 'react';

import {apolloClientNoCache} from '../../apolloClient';
import {useWorkspaceRefsContext} from '../../components/Workspace/WorkspaceRefsContext';
import {convertMetricSearchResultsToKeyInfo} from '../../components/WorkspaceDrawer/PanelsQuickAdd/hooks/useSearchedKeys';
import {useAutoOrganizePrefix} from '../../components/WorkspaceDrawer/Settings/hooks';
import {OrganizationPrefix} from '../../components/WorkspaceDrawer/Settings/types';
import type * as Generated from '../../generated/graphql';
import {EMPTY_HISTORY_KEY_INFO, type RunHistoryKeyInfo} from '../../types/run';
import {latestWinsContext} from '../../util/apolloLatestWins';
import {useNgwTruncatedPanelGenLimits} from '../../util/dynamicConfigs/ngwTruncatedPanelGenLimits';
import {
  extractMetricSearchErrorMessage,
  propagateErrorsContext,
} from '../../util/errors';
import * as Filter from '../../util/filters';
import {
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_EVALUATION_TABLES_LEGACY_NAME,
  PANEL_BANK_EVALUATION_TABLES_NAME,
  PANEL_BANK_MEDIA_NAME,
  PANEL_BANK_TABLES_NAME,
} from '../../util/panelbankTypes';
import * as QueryTS from '../../util/queryts';
import {PROJECT_RSA_READ_ENABLED_QUERY} from '../../util/rampFeatureFlags/projectRsaReadEnabled.query';
import {
  useLatchedInvalidRunFilter,
  useLatchedRejectionMessage,
} from '../../util/useInvalidRunFilter';
import {NetworkStatus} from '../graphql/apolloWithAbort';
import {useQuery} from '../graphql/query';
import {usePanelSearchDebouncedContext} from '../panelbank/PanelSearchContextProvider';
import {useHistoryKeyInfoQueryVars} from '../runs/hooks';
import {
  type MaybeTruncatedCount,
  type PanelGenHistoryKeysStoreValue,
} from './panelGenHistoryKeysStore';
import {usePinnedSectionPrefixes} from './usePinnedSectionPrefixes';
import {useRequiredMetrics} from './useRequiredMetrics';

export const PANEL_GEN_HISTORY_KEYS_QUERY = gql`
  query PanelGenHistoryKeys(
    $projectName: String!
    $entityName: String!
    $filters: JSONString
    $runLimit: Int
    $runOrder: String
    $maxPrefixGroups: Int
    $maxMetricsPerGroup: Int
    $metricNamePattern: String
    $requiredPrefixes: [String!]
    $requiredRunKeys: [String!]
    $requiredMetrics: [String!]
    $lastPrefix: Boolean
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      runs(
        filters: $filters
        first: $runLimit
        order: $runOrder
        requiredRunKeys: $requiredRunKeys
      ) {
        sectionAwareHistoryKeys(
          maxPrefixGroups: $maxPrefixGroups
          maxMetricsPerGroup: $maxMetricsPerGroup
          metricNamePattern: $metricNamePattern
          requiredPrefixes: $requiredPrefixes
          requiredMetrics: $requiredMetrics
          lastPrefix: $lastPrefix
        ) {
          lastStep
          groups {
            prefix
            matchedCount
            totalCount
            metrics {
              key
              types
              column
            }
          }
          typeCategorySections {
            category
            matchedCount
            totalCount
            hasMore
            metrics {
              key
              types
              column
            }
          }
          systemMetrics {
            key
            types
            column
          }
          matchedPrefixCount
        }
      }
    }
  }
`;

/**
 * Fetches a bounded, section-distributed sample of history keys
 * to be used for auto panel generation in a workspace.
 */
export function usePanelGenHistoryKeysQuery({skip}: {skip?: boolean}) {
  // oxlint-disable-next-line react/refs
  const latestWinsRequestKey = useRef({}).current;
  const {
    config: {runLimit, maxPrefixGroups, maxMetricsPerGroup},
    loading: limitsLoading,
  } = useNgwTruncatedPanelGenLimits();
  const {runSetRefs, customRunNamesRef} = useWorkspaceRefsContext();
  const {
    entityName,
    projectName,
    filters,
    sort,
    requiredRunKeys = [],
  } = useHistoryKeyInfoQueryVars(runSetRefs, customRunNamesRef);
  const filtersStr = JSON.stringify(Filter.toMongo(filters));
  const runOrder = QueryTS.sortToOrderString(sort);
  const {networkDebouncedSearchQuery} = usePanelSearchDebouncedContext();
  const metricNamePattern = networkDebouncedSearchQuery || undefined;
  const requiredPrefixes = usePinnedSectionPrefixes();
  // Full-screen panel name + edited-auto/custom panels' metrics, so a
  // search-scoped (or capped) fetch can't drop them and orphan their panels.
  const requiredMetrics = useRequiredMetrics();
  const lastPrefix = useAutoOrganizePrefix() === OrganizationPrefix.LastPrefix;

  const variables = useMemo<Generated.PanelGenHistoryKeysQueryVariables>(
    () => ({
      entityName,
      projectName,
      filters: filtersStr,
      runLimit,
      runOrder,
      maxPrefixGroups,
      maxMetricsPerGroup,
      metricNamePattern,
      requiredPrefixes:
        requiredPrefixes.length > 0 ? requiredPrefixes : undefined,
      requiredRunKeys: requiredRunKeys.length > 0 ? requiredRunKeys : undefined,
      requiredMetrics: requiredMetrics.length > 0 ? requiredMetrics : undefined,
      lastPrefix,
    }),
    [
      entityName,
      projectName,
      filtersStr,
      runLimit,
      runOrder,
      maxPrefixGroups,
      maxMetricsPerGroup,
      metricNamePattern,
      requiredPrefixes,
      requiredRunKeys,
      requiredMetrics,
      lastPrefix,
    ]
  );

  // Keep the accelerator read gate current in Apollo's normalized Project
  // entity. This small query is cache-backed while the larger history-key
  // response below bypasses Apollo's cache.
  useQuery<
    Generated.ProjectRunStoreAcceleratorReadEnabledQuery,
    Generated.ProjectRunStoreAcceleratorReadEnabledQueryVariables
  >(PROJECT_RSA_READ_ENABLED_QUERY, {
    variables: {entityName, projectName},
    enablePolling: true,
    context: propagateErrorsContext(),
    skip,
  });

  const query = useQuery<
    Generated.PanelGenHistoryKeysQuery,
    Generated.PanelGenHistoryKeysQueryVariables
  >(PANEL_GEN_HISTORY_KEYS_QUERY, {
    client: apolloClientNoCache,
    variables,
    enablePolling: true,
    fetchPolicy: 'no-cache',
    context: {
      ...propagateErrorsContext(),
      ...latestWinsContext(latestWinsRequestKey),
    },
    notifyOnNetworkStatusChange: true,
    // Skip until the limits config resolves: while it loads it reports the
    // defaults, indistinguishable from a project that really uses them, so
    // firing now samples at the wrong runLimit and refetches (rebuilding the
    // panel bank) once the real limits arrive. Waiting costs one round trip on
    // the first workspace load per entity; later mounts read it from cache.
    skip: skip || limitsLoading,
  });

  const isInvalidRunFilter = useLatchedInvalidRunFilter(
    query.error,
    filtersStr
  );
  const searchErrorMessage = useLatchedRejectionMessage(
    extractMetricSearchErrorMessage(query.error),
    metricNamePattern ?? ''
  );

  // Panel search change triggers re-query with updated variables.
  // While the request is in flight, Apollo may still be serving stale results.
  // Track the search query that the current result set reflects so consumers
  // can tell if the current results don't reflect the new search term yet.
  const isFetchingUpdate = query.networkStatus === NetworkStatus.setVariables;
  const resultsSearchQueryRef = useRef('');

  return useMemo(() => {
    if (!isFetchingUpdate) {
      // oxlint-disable-next-line react/refs
      resultsSearchQueryRef.current = metricNamePattern ?? '';
    }
    // A rejected run filter or panel search must not reach useAutoSyncPanelBank
    // as an error, or it throws into the workspace error boundary. Report no
    // results instead, and let the search input render the message.
    if (isInvalidRunFilter || searchErrorMessage != null) {
      return {
        loading: false,
        error: undefined,
        results: undefined,
        // oxlint-disable-next-line react/refs
        resultsSearchQuery: resultsSearchQueryRef.current,
        searchErrorMessage,
      };
    }
    return {
      loading: query.initialLoading,
      // Surface the actual Apollo error so consumers can rethrow it (preserving
      // graphQLErrors/networkError for the boundary and error reporting).
      error: query.error,
      results: query.data?.project?.runs?.sectionAwareHistoryKeys ?? undefined,
      // oxlint-disable-next-line react/refs
      resultsSearchQuery: resultsSearchQueryRef.current,
      searchErrorMessage: undefined,
    };
  }, [
    isInvalidRunFilter,
    searchErrorMessage,
    query.initialLoading,
    query.error,
    query.data,
    isFetchingUpdate,
    metricNamePattern,
  ]);
}

const TYPE_CATEGORY_SECTION_NAMES: Record<
  Generated.SectionAwareHistoryKeyTypeCategory,
  string
> = {
  CHARTS: PANEL_BANK_CHARTS_NAME,
  MEDIA: PANEL_BANK_MEDIA_NAME,
  TABLES: PANEL_BANK_TABLES_NAME,
  EVAL_TABLES_LEGACY: PANEL_BANK_EVALUATION_TABLES_LEGACY_NAME,
  EVAL_TABLES: PANEL_BANK_EVALUATION_TABLES_NAME,
};

type PanelGenHistoryKeysResults = NonNullable<
  NonNullable<
    NonNullable<Generated.PanelGenHistoryKeysQuery['project']>['runs']
  >['sectionAwareHistoryKeys']
>;

export function mapQueryResultsToStoreData(
  results: PanelGenHistoryKeysResults | undefined
): Pick<
  PanelGenHistoryKeysStoreValue,
  | 'sampledHistoryKeys'
  | 'defaultSectionsInfo'
  | 'matchedKeysBySection'
  | 'lastStep'
> {
  if (!results) {
    return {
      sampledHistoryKeys: EMPTY_HISTORY_KEY_INFO,
      defaultSectionsInfo: {numTotalMatches: 0, numExcludedMatches: 0},
      matchedKeysBySection: {},
      lastStep: undefined,
    };
  }

  // flatten/map metrics into RunHistoryKeyInfo shape (the expected format for auto panel gen)
  const allMetrics = [
    ...results.groups.flatMap(group => group.metrics),
    ...results.typeCategorySections.flatMap(section => section.metrics),
    ...results.systemMetrics,
  ];
  const keys = convertMetricSearchResultsToKeyInfo(allMetrics);
  const sampledHistoryKeys: RunHistoryKeyInfo = {
    keys,
    sortedKeys: Object.keys(keys).sort(),
  };

  // key counts matching the current run and panel filters for each section,
  // used to determine whether each section has more keys than shown
  const matchedKeysBySection: Record<string, MaybeTruncatedCount> = {};
  for (const group of results.groups) {
    // these are prefixed sections (derived from key names with slashes)
    matchedKeysBySection[group.prefix] = {
      count: group.matchedCount,
      isTruncated: group.matchedCount > group.metrics.length,
    };
  }
  for (const section of results.typeCategorySections) {
    // these are typed sections (i.e. Charts/Media/Tables, determined by key type)
    const defaultSectionName = TYPE_CATEGORY_SECTION_NAMES[section.category];
    matchedKeysBySection[defaultSectionName] = {
      count: section.matchedCount,
      isTruncated: section.matchedCount > section.metrics.length,
    };
  }

  const defaultSectionsInfo = {
    numTotalMatches:
      results.matchedPrefixCount + results.typeCategorySections.length,
    numExcludedMatches: results.matchedPrefixCount - results.groups.length,
  };

  return {
    sampledHistoryKeys,
    defaultSectionsInfo,
    matchedKeysBySection,
    // Int64 marshals as a bare JSON number, but the generated type for the
    // scalar is `any`, so narrow it here rather than at every read.
    lastStep: Number(results.lastStep),
  };
}
